import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Waktu dan Durasi - Menambah dan Mengurangi Waktu dalam Hari, Jam, Menit, dan Detik";
const DESCRIPTION =
  "Kalkulator waktu dan durasi gratis membantu Anda menambah atau mengurangi nilai waktu dalam hari, jam, menit, dan detik dengan mudah dan akurat. Hitung periode waktu, durasi proyek, jam kerja, dan waktu perjalanan dengan mudah";
const KEYWORDS =
  "kalkulator waktu, kalkulator durasi, penjumlahan waktu, pengurangan waktu, perhitungan jam, perhitungan durasi waktu, penjumlahan jam dan menit, perhitungan waktu kerja, kalkulator durasi, konversi waktu";
const OG_URL = "https://do-calculate.com/calculator/id/time-calculator/";
// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Waktu dan Durasi",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Waktu",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Bagaimana cara menggunakan kalkulator waktu?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Masukkan waktu pertama dalam hari, jam, menit, dan detik, lalu pilih operasi (tambah atau kurangi), masukkan waktu kedua, lalu tekan tombol hitung untuk mendapatkan hasilnya.",
        },
      },
      {
        "@type": "Question",
        name: "Apa saja kegunaan kalkulator waktu?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Kalkulator waktu digunakan untuk menghitung jam kerja, durasi proyek, waktu perjalanan, penjadwalan janji temu, menghitung periode pelatihan, dan semua operasi perhitungan yang berkaitan dengan waktu.",
        },
      },
    ],
  },
];

// Helper component for time input groups
const TimeInputGroup = ({ day, hour, min, sec, setDay, setHour, setMin, setSec }) => (
  <div className="d-flex flex-wrap gap-2 justify-content-center">
    <div className="form-floating mb-2" style={{ width: "80px" }}>
      <input type="number" className="form-control" placeholder="Hari" value={day} onChange={(e) => setDay(e.target.value)} min="0" />
      <label>Hari</label>
    </div>
    <div className="form-floating mb-2" style={{ width: "80px" }}>
      <input type="number" className="form-control" placeholder="Jam" value={hour} onChange={(e) => setHour(e.target.value)} min="0" max="23" />
      <label>Jam</label>
    </div>
    <div className="form-floating mb-2" style={{ width: "80px" }}>
      <input type="number" className="form-control" placeholder="Menit" value={min} onChange={(e) => setMin(e.target.value)} min="0" max="59" />
      <label>Menit</label>
    </div>
    <div className="form-floating mb-2" style={{ width: "80px" }}>
      <input type="number" className="form-control" placeholder="Detik" value={sec} onChange={(e) => setSec(e.target.value)} min="0" max="59" />
      <label>Detik</label>
    </div>
  </div>
);
function TimeCalculator() {
  const [day1, setDay1] = useState("");
  const [hour1, setHour1] = useState("");
  const [minute1, setMinute1] = useState("");
  const [second1, setSecond1] = useState("");
  const [day2, setDay2] = useState("");
  const [hour2, setHour2] = useState("");
  const [minute2, setMinute2] = useState("");
  const [second2, setSecond2] = useState("");
  const [type, setType] = useState("add");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const handleCalculateTime = () => {
    // Validate inputs
    const inputs = [day1, hour1, minute1, second1, day2, hour2, minute2, second2];
    const hasNegative = inputs.some(input => input && parseFloat(input) < 0);
    if (hasNegative) {
      setError("Harap masukkan nilai positif saja");
      setResult(null);
      return;
    }
    // Convert all to seconds
    const totalSeconds1 =
      (parseInt(day1) || 0) * 24 * 60 * 60 +
      (parseInt(hour1) || 0) * 60 * 60 +
      (parseInt(minute1) || 0) * 60 +
      (parseInt(second1) || 0);
    const totalSeconds2 =
      (parseInt(day2) || 0) * 24 * 60 * 60 +
      (parseInt(hour2) || 0) * 60 * 60 +
      (parseInt(minute2) || 0) * 60 +
      (parseInt(second2) || 0);
    let resultSeconds;
    if (type === "add") {
      resultSeconds = totalSeconds1 + totalSeconds2;
    } else if (type === "subtract") {
      resultSeconds = totalSeconds1 - totalSeconds2;
    }
    // Check if result is negative
    if (resultSeconds < 0) {
      setError("Hasil negatif. Waktu pertama harus lebih besar dari waktu kedua saat pengurangan");
      setResult(null);
      return;
    }
    // Convert back to days, hours, minutes, seconds
    const days = Math.floor(resultSeconds / (24 * 60 * 60));
    const hours = Math.floor((resultSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((resultSeconds % (60 * 60)) / 60);
    const seconds = resultSeconds % 60;
    // Calculate additional formats
    const totalHours = Math.floor(resultSeconds / (60 * 60));
    const totalMinutes = Math.floor(resultSeconds / 60);
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    setResult({
      days,
      hours,
      minutes,
      seconds,
      totalSeconds: resultSeconds,
      totalMinutes,
      totalHours,
      weeks,
      remainingDays,
      time1Total: totalSeconds1,
      time2Total: totalSeconds2,
    });
    setError("");
  };
  const handleClear = () => {
    setDay1("");
    setHour1("");
    setMinute1("");
    setSecond1("");
    setDay2("");
    setHour2("");
    setMinute2("");
    setSecond2("");
    setType("add");
    setResult(null);
    setError("");
  };
  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Waktu dan Durasi</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Waktu dan Durasi</strong> adalah alat gratis dan akurat yang membantu Anda
                <strong>menambah atau mengurangi</strong> nilai waktu dengan mudah. Anda dapat memasukkan
                waktu dalam hari, jam, menit, dan detik, dan mendapatkan hasil
                instan dan akurat. Ideal untuk menghitung jam kerja, durasi proyek, waktu perjalanan, dan semua operasi perhitungan yang berkaitan dengan waktu.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-3 text-center">
              <div className="p-3 mb-3 bg-success text-white rounded text-center">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Hitung Perbedaan Waktu</h2>
              </div>
              {/* Time 1 */}
              <div className="row mb-4 align-items-center">
                <div className="col-md-3"><strong>Waktu Pertama:</strong></div>
                <div className="col-md-9 ">
                  <TimeInputGroup day={day1} hour={hour1} min={minute1} sec={second1} setDay={setDay1} setHour={setHour1} setMin={setMinute1} setSec={setSecond1} />
                </div>
              </div>

              {/* Operation */}
              <div className="row mb-4 text-center">
                <div className="col-md-3"><strong>Operasi:</strong></div>
                <div className="col-md-9">
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="op" id="add" value="add" checked={type === "add"} onChange={(e) => setType(e.target.value)} />
                    <label className="form-check-label ms-4" htmlFor="add">Tambah (+)</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="op" id="sub" value="subtract" checked={type === "subtract"} onChange={(e) => setType(e.target.value)} />
                    <label className="form-check-label ms-4" htmlFor="sub">Kurangi (−)</label>
                  </div>
                </div>
              </div>

              {/* Time 2 */}
              <div className="row mb-4 align-items-center">
                <div className="col-md-3"><strong>Waktu Kedua:</strong></div>
                <div className="col-md-9">
                  <TimeInputGroup day={day2} hour={hour2} min={minute2} sec={second2} setDay={setDay2} setHour={setHour2} setMin={setMinute2} setSec={setSecond2} />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="text-center my-4">
                <button onClick={handleCalculateTime} className="btn btn-success btn-lg px-5 me-2 ">Hitung</button>
                <button onClick={handleClear} className="btn btn-secondary btn-lg px-5" style={{marginRight:'3px'}}>Hapus</button>
              </div>

              {error && <div className="alert alert-warning shadow-sm"><strong>Peringatan:</strong> {error}</div>}

              {/* Results Table */}
              {result && (
                <div className="result-section mt-5 animate__animated animate__fadeIn">
                  <h3 className="text-center mb-4">Hasil:</h3>
                  <div className="table-responsive">
                    <table className="table table-bordered text-center shadow-sm">
                      <thead className="table-success text-white">
                        <tr>
                          <th>Hari</th>
                          <th>Jam</th>
                          <th>Menit</th>
                          <th>Detik</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ fontSize: "20px", fontWeight: "bold" }}>
                          <td>{result.days}</td>
                          <td>{result.hours}</td>
                          <td>{result.minutes}</td>
                          <td>{result.seconds}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="row mt-4">
                    <div className="col-md-6 mb-3">
                      <div className="card h-100">
                        <div className="card-header bg-light fw-bold">Format Tambahan</div>
                        <div className="card-body">
                          <ul className="list-unstyled lh-lg">
                            {result.weeks > 0 && <li>📅 {result.weeks} minggu {result.remainingDays} hari</li>}
                            <li>⏰ {result.totalHours.toLocaleString()} jam</li>
                            <li>⏰ {result.totalMinutes.toLocaleString()} menit</li>
                            <li>⏰ {result.totalSeconds.toLocaleString()} detik</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="card h-100">
                        <div className="card-header bg-light fw-bold">Detail Perhitungan</div>
                        <div className="card-body" style={{ fontSize: "14px" }}>
                          <p>Waktu 1: {result.time1Total.toLocaleString()} detik</p>
                          <p>Operasi: {type === "add" ? "Tambah" : "Kurang"}</p>
                          <p>Waktu 2: {result.time2Total.toLocaleString()} detik</p>
                          <hr />
                          <p className="fw-bold text-success">Total: {result.totalSeconds.toLocaleString()} detik</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <RelatedLinks />
          </div>
        </div>

        <div className="col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa Itu Kalkulator Waktu dan Durasi?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kalkulator Waktu dan Durasi</strong> adalah alat elektronik gratis
                  yang membantu Anda melakukan operasi aritmatika pada waktu dengan cara yang akurat dan cepat.
                  Kalkulator ini memungkinkan Anda:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menjumlahkan Periode Waktu:</strong> Menambahkan dua waktu bersama (seperti menjumlahkan jam kerja)
                  </li>
                  <li>
                    <strong>Mengurangi Periode Waktu:</strong> Menghitung perbedaan antara dua waktu
                  </li>
                  <li>
                    <strong>Menangani Berbagai Satuan:</strong> Hari, jam, menit, dan detik
                  </li>
                  <li>
                    <strong>Konversi Otomatis:</strong> Mengonversi hasil ke berbagai format
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Cara Menggunakan Kalkulator:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Masukkan waktu pertama dalam hari, jam, menit, dan detik</li>
                  <li>Pilih operasi (tambah atau kurangi)</li>
                  <li>Masukkan waktu kedua dengan cara yang sama</li>
                  <li>Tekan tombol "Hitung"</li>
                  <li>Hasil akan langsung muncul dalam berbagai format</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 Catatan:</strong> Anda dapat membiarkan kolom apa pun kosong, dan kolom tersebut akan dianggap nol secara otomatis. Misalnya, jika Anda ingin menjumlahkan 2 jam 30 menit, biarkan kolom hari dan detik kosong.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Penggunaan Kalkulator Waktu</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Dalam Pekerjaan dan Bisnis:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menghitung Jam Kerja:</strong> Menjumlahkan jam kerja harian atau mingguan
                  </li>
                  <li>
                    <strong>Manajemen Proyek:</strong> Menghitung total durasi proyek
                  </li>
                  <li>
                    <strong>Penjadwalan Janji:</strong> Menghitung waktu rapat dan acara
                  </li>
                  <li>
                    <strong>Menghitung Cuti:</strong> Menjumlahkan hari cuti yang terakumulasi
                  </li>
                  <li>
                    <strong>Faktur:</strong> Menghitung jam kerja untuk menerbitkan faktur
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam Pendidikan dan Pelatihan:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menghitung Jam Belajar:</strong> Melacak waktu belajar dan review
                  </li>
                  <li>
                    <strong>Durasi Kursus Pelatihan:</strong> Menghitung total jam pelatihan
                  </li>
                  <li>
                    <strong>Jadwal Ujian:</strong> Menghitung waktu ujian
                  </li>
                  <li>
                    <strong>Perencanaan Kurikulum:</strong> Mendistribusikan waktu untuk mata pelajaran
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam Kehidupan Sehari-hari:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Waktu Perjalanan:</strong> Menghitung durasi perjalanan
                  </li>
                  <li>
                    <strong>Memasak dan Memanggang:</strong> Menjumlahkan waktu persiapan dan memasak
                  </li>
                  <li>
                    <strong>Olahraga:</strong> Menghitung total waktu latihan
                  </li>
                  <li>
                    <strong>Tidur:</strong> Melacak jam tidur
                  </li>
                  <li>
                    <strong>Aktivitas Rekreasi:</strong> Menghitung waktu hobi
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam Bidang Medis:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Durasi Operasi Bedah:</strong> Menghitung waktu operasi
                  </li>
                  <li>
                    <strong>Periode Pengobatan:</strong> Menghitung durasi terapi fisik atau obat
                  </li>
                  <li>
                    <strong>Jadwal Obat:</strong> Menghitung periode minum obat
                  </li>
                  <li>
                    <strong>Shift Kerja:</strong> Menghitung jam kerja dokter dan perawat
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Memahami Satuan Waktu</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Untuk memahami cara kerja kalkulator waktu, penting untuk mengetahui hubungan antara satuan waktu yang berbeda:
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Satuan Waktu Dasar:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Detik:</strong> Satuan dasar waktu dalam Sistem Internasional
                  </li>
                  <li>
                    <strong>Menit:</strong> 1 menit = 60 detik
                  </li>
                  <li>
                    <strong>Jam:</strong> 1 jam = 60 menit = 3.600 detik
                  </li>
                  <li>
                    <strong>Hari:</strong> 1 hari = 24 jam = 1.440 menit = 86.400 detik
                  </li>
                  <li>
                    <strong>Minggu:</strong> 1 minggu = 7 hari = 168 jam
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Tabel Konversi Cepat:
                </h3>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Dari</th>
                        <th>Ke</th>
                        <th>Rumus</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Menit</td>
                        <td>Detik</td>
                        <td>× 60</td>
                      </tr>
                      <tr>
                        <td>Jam</td>
                        <td>Menit</td>
                        <td>× 60</td>
                      </tr>
                      <tr>
                        <td>Jam</td>
                        <td>Detik</td>
                        <td>× 3.600</td>
                      </tr>
                      <tr>
                        <td>Hari</td>
                        <td>Jam</td>
                        <td>× 24</td>
                      </tr>
                      <tr>
                        <td>Hari</td>
                        <td>Menit</td>
                        <td>× 1.440</td>
                      </tr>
                      <tr>
                        <td>Hari</td>
                        <td>Detik</td>
                        <td>× 86.400</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Bagaimana Kalkulator Bekerja:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Mengonversi semua nilai yang dimasukkan ke dalam detik</li>
                  <li>Melakukan operasi aritmatika (tambah atau kurangi) pada total detik</li>
                  <li>Mengonversi hasil kembali ke hari, jam, menit, dan detik</li>
                  <li>Menampilkan hasil dalam berbagai format untuk memudahkan penggunaan</li>
                </ol>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis Penggunaan Kalkulator Waktu</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Contoh 1: Menghitung Jam Kerja Mingguan
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Masalah:</strong> Anda bekerja 7 jam 45 menit setiap hari selama 5 hari. Berapa jam Anda bekerja dalam seminggu?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Masukkan waktu pertama: 0 hari, 7 jam, 45 menit, 0 detik</li>
                  <li>Pilih "Tambah"</li>
                  <li>Masukkan waktu yang sama 4 kali lagi (atau kalikan hasilnya × 5)</li>
                  <li>
                    Hasil: 1 hari, 14 jam, 45 menit (atau 38 jam 45 menit)
                  </li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 2: Menghitung Waktu Perjalanan
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Masalah:</strong> Perjalanan Anda terdiri dari penerbangan 5 jam 30 menit, waktu tunggu 2 jam 15 menit, dan penerbangan lain 3 jam 50 menit. Berapa total waktu perjalanan?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Masukkan waktu pertama: 0 hari, 5 jam, 30 menit, 0 detik</li>
                  <li>Pilih "Tambah"</li>
                  <li>Masukkan waktu kedua: 0 hari, 2 jam, 15 menit, 0 detik</li>
                  <li>Hasil: 7 jam 45 menit</li>
                  <li>Ulangi dengan penerbangan ketiga untuk mendapatkan total akhir</li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 3: Menghitung Waktu Tersisa dalam Proyek
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Masalah:</strong> Proyek Anda membutuhkan 40 jam kerja, dan Anda telah bekerja 28 jam 30 menit. Berapa sisa waktu?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Masukkan waktu pertama: 1 hari, 16 jam, 0 menit, 0 detik (40 jam)</li>
                  <li>Pilih "Kurang"</li>
                  <li>Masukkan waktu kedua: 1 hari, 4 jam, 30 menit, 0 detik (28,5 jam)</li>
                  <li>Hasil: 0 hari, 11 jam, 30 menit, 0 detik</li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 4: Menghitung Waktu Latihan
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Masalah:</strong> Latihan harian Anda terdiri dari: 15 menit pemanasan, 45 menit latihan utama, 20 menit latihan kekuatan, 10 menit pendinginan. Berapa total waktu latihan?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong> Jumlahkan semua waktu untuk mendapatkan 1 jam 30 menit
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips Menggunakan Kalkulator Waktu Secara Efektif</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Tips Umum:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Gunakan Satuan yang Sesuai:</strong> Pilih satuan yang sesuai dengan konteks penggunaan Anda (hari untuk proyek panjang, jam untuk pekerjaan harian)
                  </li>
                  <li>
                    <strong>Periksa Input:</strong> Pastikan Anda memasukkan nilai yang benar sebelum menghitung
                  </li>
                  <li>
                    <strong>Kolom Kosong = Nol:</strong> Tidak perlu mengisi semua kolom, biarkan yang tidak perlu kosong
                  </li>
                  <li>
                    <strong>Gunakan Berbagai Format:</strong> Tinjau hasil dalam berbagai format untuk memilih yang paling sesuai
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Untuk Menghindari Kesalahan:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Ingat bahwa 1 jam = 60 menit (bukan 100)</li>
                  <li>1 menit = 60 detik (bukan 100)</li>
                  <li>1 hari = 24 jam</li>
                  <li>Saat mengurangi, pastikan waktu pertama lebih besar dari waktu kedua</li>
                  <li>Gunakan nilai positif saja</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Untuk Penggunaan Profesional:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Simpan catatan perhitungan penting</li>
                  <li>Ambil tangkapan layar untuk referensi nanti</li>
                  <li>Gunakan kalkulator dengan spreadsheet untuk analisis yang lebih baik</li>
                  <li>Tinjau hasil dalam berbagai format untuk memastikan akurasi</li>
                </ul>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ Fitur:</strong> Kalkulator waktu bekerja secara instan dan tanpa perlu internet setelah halaman dimuat, membuatnya ideal untuk digunakan di mana saja!
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Informasi Menarik tentang Waktu</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Fakta Menarik:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Jam Atom:</strong> Jam paling akurat di dunia mungkin meleset satu detik setiap 100 juta tahun!
                  </li>
                  <li>
                    <strong>Detik Kabisat:</strong> Terkadang detik kabisat ditambahkan untuk menyelaraskan jam atom dengan rotasi bumi
                  </li>
                  <li>
                    <strong>Waktu Relatif:</strong> Menurut teori Einstein, waktu berjalan lebih lambat semakin cepat kecepatan atau gravitasi
                  </li>
                  <li>
                    <strong>Di Luar Angkasa:</strong> Astronot hidup lebih lambat sepersekian detik karena kecepatan tinggi mereka
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Angka Menarik:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Dalam setahun: sekitar 31.536.000 detik</li>
                  <li>Dalam sehari: 86.400 detik</li>
                  <li>Dalam satu jam: 3.600 detik</li>
                  <li>Manusia menghabiskan sepertiga hidupnya untuk tidur (sekitar 229.961 jam dalam 80 tahun)</li>
                  <li>Rata-rata usia manusia: sekitar 700.800 jam (80 tahun)</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam Budaya yang Berbeda:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Kalender Cina:</strong> Menggunakan siklus 60 tahun
                  </li>
                  <li>
                    <strong>Kalender Maya:</strong> Sangat akurat dan kompleks
                  </li>
                  <li>
                    <strong>Orang Mesir Kuno:</strong> Di antara yang pertama membagi hari menjadi 24 jam
                  </li>
                  <li>
                    <strong>Bangsa Babilonia:</strong> Yang pertama menggunakan sistem 60 (60 menit, 60 detik)
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            Pertanyaan Umum
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bagaimana cara menggunakan kalkulator waktu?
                </h3>
              </div>
              <div className="info-section-body">
                Menggunakan kalkulator waktu sangat sederhana:
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    Masukkan waktu pertama di kolom yang disediakan (Anda dapat membiarkan kolom apa pun kosong jika tidak membutuhkannya)
                  </li>
                  <li>Pilih operasi: tambah (+) atau kurang (−)</li>
                  <li>Masukkan waktu kedua dengan cara yang sama</li>
                  <li>Tekan tombol "Hitung"</li>
                  <li>
                    Hasil akan langsung muncul dalam tabel yang jelas, dengan format tambahan di bagian yang dapat diperluas
                  </li>
                </ol>
                Kolom kosong diperlakukan secara otomatis sebagai nol, jadi tidak perlu mengisi semua kolom.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Apa perbedaan antara menjumlahkan dan mengurangi waktu?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Penjumlahan Waktu (+):</strong>
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Digunakan untuk menambahkan dua periode waktu bersama</li>
                  <li>
                    Contoh: 2 jam + 3 jam = 5 jam (menghitung total jam kerja)
                  </li>
                  <li>
                    Berguna untuk menghitung total kumulatif dari beberapa periode
                  </li>
                </ul>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  <strong>Pengurangan Waktu (−):</strong>
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Digunakan untuk menghitung perbedaan antara dua waktu</li>
                  <li>
                    Contoh: 5 jam − 2 jam = 3 jam (menghitung waktu tersisa)
                  </li>
                  <li>
                    Waktu pertama harus lebih besar dari waktu kedua untuk menghindari hasil negatif
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Dapatkah saya menggunakan kalkulator untuk menghitung jam kerja?
                </h3>
              </div>
              <div className="info-section-body">
                Ya! Kalkulator waktu sangat ideal untuk menghitung jam kerja. Anda dapat:
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>Menghitung satu hari:</strong> Masukkan waktu mulai dan selesai dan kurangkan
                  </li>
                  <li>
                    <strong>Menjumlahkan beberapa hari:</strong> Hitung jam setiap hari lalu jumlahkan
                  </li>
                  <li>
                    <strong>Menghitung minggu:</strong> Kalikan jam satu hari dengan jumlah hari kerja
                  </li>
                  <li>
                    <strong>Mengurangi waktu istirahat:</strong> Kurangi waktu makan siang atau istirahat dari total waktu
                  </li>
                </ul>
                Hasil akan muncul dalam berbagai format yang berguna untuk faktur dan laporan penggajian.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Mengapa 1 jam = 60 menit, bukan 100?
                </h3>
              </div>
              <div className="info-section-body">
                Ini berasal dari bangsa Babilonia kuno yang menggunakan sistem sexagesimal (basis 60) ribuan tahun yang lalu:
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>Kemudahan Pembagian:</strong> Angka 60 habis dibagi 1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30, 60
                  </li>
                  <li>
                    <strong>Matematika Astronomi:</strong> Sesuai dengan perhitungan astronomi dan kalender
                  </li>
                  <li>
                    <strong>Tradisi Sejarah:</strong> Penggunaannya berlanjut lintas peradaban karena efektivitasnya
                  </li>
                </ul>
                Sistem ini masih digunakan hingga hari ini untuk mengukur waktu dan sudut!
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Apakah kalkulator ini akurat?
                </h3>
              </div>
              <div className="info-section-body">
                Ya, kalkulator waktu sangat akurat. Kalkulator menggunakan:
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>Algoritma Akurat:</strong> Mengonversi semuanya ke detik lalu kembali ke format yang diinginkan
                  </li>
                  <li>
                    <strong>Penanganan Konversi yang Benar:</strong> Memperhitungkan 60 detik per menit, 60 menit per jam, 24 jam per hari
                  </li>
                  <li>
                    <strong>Perhitungan Instan:</strong> Hasil langsung tanpa kesalahan pembulatan
                  </li>
                  <li>
                    <strong>Validasi:</strong> Memberi tahu Anda jika Anda memasukkan nilai yang tidak valid
                  </li>
                </ul>
                Anda dapat mengandalkan hasilnya dengan penuh keyakinan untuk penggunaan profesional dan pribadi.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bagaimana jika saya mendapatkan hasil negatif?
                </h3>
              </div>
              <div className="info-section-body">
                Jika Anda mendapatkan pesan kesalahan yang mengatakan "Hasil negatif", ini berarti:
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    Saat memilih operasi <strong>pengurangan</strong>, waktu kedua lebih besar dari waktu pertama
                  </li>
                  <li>Untuk mengatasi masalah ini, Anda memiliki dua pilihan:</li>
                  <ul>
                    <li>
                      <strong>Opsi 1:</strong> Balikkan input (masukkan waktu yang lebih besar terlebih dahulu)
                    </li>
                    <li>
                      <strong>Opsi 2:</strong> Ubah operasi dari "kurang" menjadi "tambah" jika Anda ingin menjumlahkan
                    </li>
                  </ul>
                </ul>
                Contoh: Jika Anda memasukkan 2 jam − 5 jam, kalkulator akan memperingatkan Anda. Solusinya: masukkan 5 jam − 2 jam = 3 jam.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bagaimana cara mengonversi hasil ke format yang berbeda?
                </h3>
              </div>
              <div className="info-section-body">
                Setelah mendapatkan hasil, klik bagian "Format Tambahan" untuk melihat hasil dalam berbagai cara:
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>Dalam minggu dan hari:</strong> Berguna untuk proyek panjang
                  </li>
                  <li>
                    <strong>Total jam:</strong> Berguna untuk menghitung faktur
                  </li>
                  <li>
                    <strong>Total menit:</strong> Berguna untuk tugas-tugas pendek
                  </li>
                  <li>
                    <strong>Total detik:</strong> Untuk akurasi maksimum
                  </li>
                </ul>
                Pilih format yang sesuai dengan kebutuhan Anda!
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Dapatkah saya menggunakan kalkulator di ponsel?
                </h3>
              </div>
              <div className="info-section-body">
                Ya! Kalkulator waktu dirancang untuk bekerja secara efisien di semua perangkat:
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>Ponsel Pintar:</strong> Antarmuka responsif yang beradaptasi dengan layar kecil
                  </li>
                  <li>
                    <strong>Tablet:</strong> Pengalaman ideal untuk layar sedang
                  </li>
                  <li>
                    <strong>Komputer:</strong> Memanfaatkan ruang layar yang besar
                  </li>
                </ul>
                Kalkulator bekerja secara instan tanpa perlu internet setelah halaman dimuat, membuatnya ideal untuk digunakan di mana saja!
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default TimeCalculator;